import useAlchemystStoreForAi from '@/hooks/ai/client/useAlchemystStoreForAi';
import useContextStore from '@/hooks/useContextStore';
import { ContextNode, FileMetadata } from '@/types/context/context-page';
import { fetchWithRewrites } from '@/utils/fetchWithRewrites';
import DeleteIcon from '@mui/icons-material/Delete';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import { Box, CircularProgress, IconButton, Stack, Typography } from '@mui/material';
import { DataGridPro, GridColDef, GridToolbar, GridValueFormatter } from '@mui/x-data-grid-pro';
import { ReactNode, useEffect, useMemo, useState } from 'react';

// Format file size to human-readable format
const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} bytes`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

// Format date to readable format
const formatDate = (timestamp: string | number): string => {
  const date = typeof timestamp === 'string' ? new Date(timestamp) : new Date(timestamp);
  return date.toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
};

interface DocumentInfo {
  id: string;
  fileName: string;
  fileSize: number;
  fileSizeFormatted: string;
  fileType: string;
  lastModified: string;
  modifiedDate: string;
  isDeleting?: boolean;
}

function Documents() {
  const [documents, setDocuments] = useState<DocumentInfo[]>([]);
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState<string[]>([]);
  
  const { contextNodes } = useContextStore((store) => ({
    contextNodes: store.contextNodes ?? [],
  }));

  const { setStoreState, snackbarEntries } = useAlchemystStoreForAi(
    (store) => ({
      setStoreState: store.setStoreState,
      snackbarEntries: store.snackbarEntries,
    })
  );

  useEffect(() => {
    async function fetchDocuments() {
      setLoading(true);
      try {
        const res = await fetchWithRewrites('/api/v1/context/view/docs', {
          method: 'GET',
        });
        const data = await res.json();

        if (res.ok) {
          // Create document info objects from the new API response format
          const docInfos: DocumentInfo[] = data.documents.map((doc: any) => {
            return {
              id: doc._id,
              fileName: doc._id,
              fileSize: doc.size,
              fileSizeFormatted: formatFileSize(doc.size),
              fileType: doc.doc_type,
              lastModified: doc.last_modified_at,
              modifiedDate: formatDate(doc.last_modified_at),
              isDeleting: false
            };
          });
          
          setDocuments(docInfos);
        }

        console.log('The Data', data);
      } catch (error) {
        console.error('Error fetching documents:', error);
      } finally {
        setLoading(false);
      }
    }

    fetchDocuments();
  }, [contextNodes]);

  // Update the isDeleting status based on the deleting array
  useEffect(() => {
    setDocuments(prevDocs => 
      prevDocs.map(doc => ({
        ...doc,
        isDeleting: deleting.includes(doc.fileName)
      }))
    );
  }, [deleting]);

  const handleDelete = async (fileName: string) => {
    try {
      setDeleting((prev) => [...prev, fileName]);
      setStoreState({
        snackbarEntries: [
          ...snackbarEntries,
          {
            message: 'Deleting file..',
            severity: 'info',
            notificationId: crypto.randomUUID(),
            type: 'alert',
          },
        ],
      });
      const res = await fetchWithRewrites('/api/v1/context/delete', {
        method: 'POST',
        body: JSON.stringify({
          source: fileName,
          by_doc: true,
          by_id: false,
        }),
      });

      if (!res.ok) {
        setStoreState({
          snackbarEntries: [
            ...snackbarEntries,
            {
              message: 'Error deleting files..',
              severity: 'info',
              notificationId: crypto.randomUUID(),
              type: 'alert',
            },
          ],
        });
      }
      
      // Update documents list
      setDocuments(prevDocs => prevDocs.filter(doc => doc.fileName !== fileName));
    } catch (error) {
      console.log('Error: ', error);
    } finally {
      setDeleting((prev) => prev.filter((f) => f !== fileName));
      setStoreState({
        snackbarEntries: [
          ...snackbarEntries,
          {
            message: 'File has been deleted successfully..',
            severity: 'info',
            notificationId: crypto.randomUUID(),
            type: 'alert',
          },
        ],
      });
    }
  };

  const columns = useMemo<GridColDef<DocumentInfo>[]>(() => [
    {
      field: 'fileName',
      headerName: 'Name',
      flex: 1,
      renderCell: (params) => (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ width: '100%', height: '100%' }}>
          <InsertDriveFileIcon 
            color="primary" 
            fontSize="small" 
            sx={{ opacity: params.row.isDeleting ? 0.4 : 1 }}
          />
          <Typography 
            sx={{ 
              textDecoration: params.row.isDeleting ? 'line-through' : 'none',
              opacity: params.row.isDeleting ? 0.4 : 1,
              transition: 'opacity 0.3s ease'
            }}
          >
            {params.value}
          </Typography>
        </Stack>
      )
    },
    {
      field: 'fileType',
      headerName: 'Type',
      flex: 0.5,
      align: 'right',
      headerAlign: 'right',
    },
    {
      field: 'fileSize',
      headerName: 'Size',
      flex: 0.5,
      align: 'right',
      headerAlign: 'right',
      type: 'number',
      valueFormatter: (value) => value ? formatFileSize(value as number) : '-',
      renderCell: (params) => (
        <Typography 
          sx={{ 
            opacity: params.row.isDeleting ? 0.4 : 1,
            transition: 'opacity 0.3s ease',
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end'
          }}
        >
          {params.row.fileSizeFormatted}
        </Typography>
      )
    },
    {
      field: 'lastModified',
      headerName: 'Modified',
      flex: 0.5,
      align: 'right',
      headerAlign: 'right',
      valueFormatter: (value) => value ? formatDate(value as string) : '-',
      renderCell: (params) => (
        <Typography 
          sx={{ 
            opacity: params.row.isDeleting ? 0.4 : 1,
            transition: 'opacity 0.3s ease',
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end'
          }}
        >
          {params.row.modifiedDate}
        </Typography>
      )
    },
    {
      field: 'actions',
      headerName: 'Actions',
      flex: 0.3,
      align: 'right',
      headerAlign: 'right',
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Box
          sx={{
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end'
          }}
        >
          {params.row.isDeleting ? (
            <CircularProgress size={24} />
          ) : (
            <IconButton 
              size="small" 
              onClick={() => handleDelete(params.row.fileName)}
              sx={{ color: 'error.main' }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
      )
    }
  ], []);

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>Documents</Typography>
      
      <DataGridPro
        slots={{
          toolbar: GridToolbar,
        }}
        slotProps={{
          toolbar: {
            showQuickFilter: true,
            quickFilterProps: { debounceMs: 300 },
          },
        }}
        sx={{
          width: '100%',
          height: '50vh',
          overflowY: 'auto',
          overflowX: 'scroll',
          '& .MuiDataGrid-cell': {
            display: 'flex',
            alignItems: 'center',
          }
        }}
        rows={documents}
        columns={columns}
        loading={loading}
        disableRowSelectionOnClick
        pageSizeOptions={[10, 25, 50]}
        initialState={{
          pagination: {
            paginationModel: { pageSize: 10, page: 0 },
          },
        }}
      />
    </Box>
  );
}

export default Documents;
